import { Typography, Stack, Box } from '@mui/material';
import {
  WorkflowRequestFragment,
  WorkflowRequestStatus,
  WorkflowWithSectionsFragment,
} from 'gql/graphql';
import {
  useUpdateWorklowRequest,
  useWorkflowRequestQuery,
  useWorkflowRequestsQuery,
} from 'hooks/workflow-request-hooks';
import { useEffect, useMemo, useState } from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom-latest';
import LoadingIndicator from 'common/LoadingIndicator';
import { PageProps } from 'ui/Page/Page';
import {
  IN_APP,
  REQUESTS_PAGE_ROUTES,
} from 'components/Requests/requests.constants';
import { DASHBOARD_ROUTE } from 'constants/constants';
import SendRequest from 'components/Requests/components/SendRequest/SendRequest';
import { TaxDeliveryDispatchView } from 'components/Requests/components/TaxDelivery/components/TaxDeliveryDispatchView/TaxDeliveryDispatchView';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  mapWorkflowRequestToEventProperties,
  useTrackRequestEvent,
} from 'Mixpanel/useTrackRequestEvent';
import { useRequestExperience } from 'hooks/use-experiences-hook';
import { WorkflowViewer } from './WorkflowViewer';
import { ErrorMessage } from './components/ErrorMessage/ErrorMessage';
import { WorkflowViewWrapper } from './components/WorkflowViewWrapper/WorkflowViewWrapper';
import { WorkflowViewHeader } from './components/WorkflowViewHeader/WorkflowViewHeader';
import { WorkflowViewFooter } from './components/WorkflowViewFooter/WorkflowViewFooter';

export interface WorkflowViewProps extends Omit<PageProps, 'children'> {
  title?: string;
  isDispatch?: boolean;
}

export const WorkflowView = ({
  title,
  isDispatch = false,
}: WorkflowViewProps) => {
  const { isNewRequestExperienceEnabled } = useRequestExperience();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const template = searchParams.get('template');
  const { workflowOpened: workflowOpenedFlag, requestOptInBanners } =
    useFlags();
  const { trackRequestEvent } = useTrackRequestEvent();

  const {
    data: workflowRequestData,
    isInitialLoading: requestLoading,
    error: workflowRequestError,
    isError: isWorkflowRequestError,
  } = useWorkflowRequestQuery(id as string);

  const {
    data: workflowRequestsData,
    isLoading: wfRequestDataIsLoading,
    fetchStatus: wfRequestDataFetchStatus,
  } = useWorkflowRequestsQuery(
    {
      filter: 'my_requests',
    },
    {
      enabled: true,
    },
  );

  const { mutate: updateWorkflowRequest } = useUpdateWorklowRequest();

  const workflowRequest = workflowRequestData?.workflowRequest;

  const workflow = workflowRequest?.workflow;

  // if the logged in user is the assigned user for the request, they can edit the request without prompting
  // assumption atm is if a client can see this request they are the assigned user
  // if the user needs to be prompted to edit we want to start with the input disabled
  const [disableInputState, setDisableInputState] = useState<boolean>(true);
  const [promptEdit, setPromptEdit] = useState<boolean>(false);

  const isAssignee = useMemo(
    () =>
      workflowRequestsData?.workflowRequests?.collection.some(
        (request) => request.id === workflowRequest?.id,
      ),
    [workflowRequest?.id, workflowRequestsData?.workflowRequests?.collection],
  );

  useEffect(() => {
    const wfRequestDataLoading =
      wfRequestDataIsLoading || wfRequestDataFetchStatus !== 'idle';

    const prompt = wfRequestDataLoading
      ? false
      : !isAssignee && Boolean(workflowRequest) && !isDispatch;

    setPromptEdit(prompt);
  }, [
    id,
    isAssignee,
    isDispatch,
    wfRequestDataFetchStatus,
    wfRequestDataIsLoading,
    workflowOpenedFlag,
    workflowRequest,
    workflowRequestsData?.workflowRequests,
  ]);

  useEffect(() => {
    if (
      workflowRequest?.status === WorkflowRequestStatus.Requested &&
      workflowOpenedFlag &&
      isAssignee
    ) {
      updateWorkflowRequest({
        workflowRequestId: id,
        status: WorkflowRequestStatus.Open,
      });
    }
  }, [
    id,
    isAssignee,
    updateWorkflowRequest,
    workflowOpenedFlag,
    workflowRequest?.status,
  ]);

  useEffect(() => {
    if (workflowRequestData) {
      trackRequestEvent(
        'request_opened',
        mapWorkflowRequestToEventProperties(
          workflowRequestData?.workflowRequest,
        ),
      );
    }
  }, [workflowRequestData, trackRequestEvent]);

  const handleGoBack = async () => {
    // remove once requestOptInBanners flag is no longer needed
    // the user has come from the new request page so location.key will be 'default'
    // but they should be taken back to the previous page (new requests) not the dashboard
    if (requestOptInBanners && (await isNewRequestExperienceEnabled())) {
      navigate(-1);
      return;
    }

    if (location.key !== 'default') {
      navigate(-1);
    } else {
      navigate(DASHBOARD_ROUTE);
    }
  };

  const handleEditRequest = () => {
    navigate(
      `${REQUESTS_PAGE_ROUTES.builder}/${workflow?.id}${
        template ? `?prepareOnSave=true` : ''
      }`,
      {
        state: { from: IN_APP },
      },
    );
  };

  const handleSaveAndClose = () => {
    navigate(REQUESTS_PAGE_ROUTES.requests);
  };

  const workflowIsEditable =
    workflowRequest?.rpaDocuments?.length === 0 ||
    workflowRequest?.rpaDocuments === null ||
    !workflowRequest;

  if (requestLoading) {
    return <LoadingIndicator loading />;
  }

  if (isWorkflowRequestError) {
    return (
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        mt={10}
        sx={{ height: 'calc(100vh - 100px)' }}
      >
        {/*  @ts-ignore */}
        <ErrorMessage error={workflowRequestError[0]} />
      </Stack>
    );
  }

  if (!workflow && !workflowRequest) {
    <Stack direction="row" alignItems="center" justifyContent="center" mt={10}>
      <Typography variant="h4">No data found</Typography>
    </Stack>;
  }

  if (workflow?.type === 'TaxDeliveryWorkflow' && isDispatch && workflowRequest)
    return <TaxDeliveryDispatchView workflowRequest={workflowRequest} />;

  return (
    <WorkflowViewWrapper
      slots={{
        Header: (
          <WorkflowViewHeader
            workflowRequest={workflowRequest}
            isDispatch={isDispatch}
            promptEdit={promptEdit}
            disableInputState={disableInputState}
            setDisableInputState={setDisableInputState}
            onGoBack={handleGoBack}
            title={title}
          />
        ),
        Footer: (
          <WorkflowViewFooter
            onSubmitDisabled={promptEdit || disableInputState || isDispatch}
            onSaveAndExitDisabled={
              promptEdit || disableInputState || isDispatch
            }
            onCompleteDisabled={!promptEdit}
            workflowRequest={workflowRequest}
          />
        ),
        SideBar: isDispatch ? (
          <Box height="calc(100vh - 72px)">
            <SendRequest
              workflow={workflowRequest?.workflow}
              workflowRequest={workflowRequest}
              onClose={handleSaveAndClose}
            />
          </Box>
        ) : undefined,
      }}
    >
      <WorkflowViewer
        workflow={workflow as WorkflowWithSectionsFragment}
        workflowRequest={workflowRequest as WorkflowRequestFragment}
        disableInputState={disableInputState}
        onEditWorkflowClick={
          isDispatch && workflowIsEditable ? handleEditRequest : undefined
        }
        isDispatch={isDispatch}
        allowResponseEdit={promptEdit}
      />
    </WorkflowViewWrapper>
  );
};
